import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Purchase() {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    gifterEmail: '',
    gifterName: '',
  });
  const [isGift, setIsGift] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const headerReveal = useScrollReveal({ threshold: 0.2 });
  const cardReveal = useScrollReveal({ threshold: 0.2 });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.email) {
      setError(isGift ? 'Prašome įvesti gavėjo el. pašto adresą' : 'Prašome įvesti savo el. pašto adresą');
      return;
    }

    if (!formData.name) {
      setError(isGift ? 'Prašome įvesti gavėjo vardą' : 'Prašome įvesti savo vardą');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Prašome įvesti teisingą el. pašto adresą');
      return;
    }

    if (isGift) {
      if (!formData.gifterEmail) {
        setError('Prašome įvesti savo el. pašto adresą');
        return;
      }
      if (!emailRegex.test(formData.gifterEmail)) {
        setError('Prašome įvesti teisingą el. pašto adresą');
        return;
      }
      if (!formData.gifterName) {
        setError('Prašome įvesti savo vardą');
        return;
      }
    }

    setIsLoading(true);

    try {
      const { data, error: functionError } = await supabase.functions.invoke(
        'create-checkout',
        {
          body: {
            email: formData.email,
            name: formData.name,
            isGift,
            gifterEmail: isGift ? formData.gifterEmail : undefined,
            gifterName: isGift ? formData.gifterName : undefined,
            lang: 'lt', // Lithuanian version
          },
        }
      );

      if (functionError) throw functionError;

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Nepavyko sukurti mokėjimo');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Įvyko klaida. Prašome bandyti dar kartą.');
      setIsLoading(false);
    }
  };

  return (
    <section id="purchase" className="relative py-32 bg-gradient-to-br from-indigo-950 via-purple-900 to-indigo-900 overflow-hidden">
      {/* Enhanced animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-10 w-32 h-32 text-amber-400/10 animate-float">
          <i className="ri-star-line text-6xl"></i>
        </div>
        <div className="absolute bottom-40 left-20 w-24 h-24 text-purple-300/10 animate-float-delayed">
          <i className="ri-moon-line text-5xl"></i>
        </div>
        <div className="absolute top-1/2 right-1/4 w-28 h-28 text-pink-300/10 animate-float">
          <i className="ri-sparkling-2-line text-5xl"></i>
        </div>
        {/* Additional floating particles */}
        <div className="absolute top-1/3 left-1/4 w-20 h-20 text-purple-400/10 animate-float-delayed">
          <i className="ri-heart-3-line text-4xl"></i>
        </div>
        <div className="absolute bottom-1/3 right-1/3 w-16 h-16 text-pink-400/10 animate-float">
          <i className="ri-gift-line text-3xl"></i>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div 
            ref={headerReveal.ref}
            className={`text-center mb-16 ${headerReveal.isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
          >
            <div className="inline-block bg-gradient-to-r from-purple-400 to-pink-400 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6 animate-pulse-glow">
              ⚡ SPECIALUS PASIŪLYMAS
            </div>
            <h2 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6">
              Pradėkite Savo Kelionę Šiandien
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Įsigykite knygą ir gaukite ją <strong className="text-amber-400 animate-shimmer">iš karto</strong> PDF formatu į savo el. paštą
            </p>
          </div>

          {/* Purchase Card with entrance animation */}
          <div 
            ref={cardReveal.ref}
            className={`bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden hover:scale-[1.02] transition-all duration-500 ${
              cardReveal.isVisible ? 'animate-scale-in' : 'opacity-0'
            }`}
          >
            <div className="grid md:grid-cols-2">
              {/* Left side - Book info */}
              <div className="bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 p-10 text-white flex flex-col justify-center relative overflow-hidden animate-gradient">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-pink-300/20 rounded-full blur-2xl"></div>
                
                <div className="relative z-10">
                  <div className="mb-8">
                    <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
                      📘 Skaitmeninė Knyga
                    </div>
                    <h3 className="text-3xl font-serif font-bold mb-4">
                      Keturi Vartai į Save
                    </h3>
                    <p className="text-purple-50 text-lg mb-8">
                      PDF formatas • Iš karto po apmokėjimo • Skaitykite bet kuriame įrenginyje
                    </p>
                  </div>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-center gap-3 group">
                      <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300">
                        <i className="ri-checkbox-circle-fill text-xl"></i>
                      </div>
                      <span>Momentinis pristatymas į el. paštą</span>
                    </div>
                    <div className="flex items-center gap-3 group">
                      <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300">
                        <i className="ri-download-cloud-line text-xl"></i>
                      </div>
                      <span>Atsisiųskite ir skaitykite bet kada</span>
                    </div>
                    <div className="flex items-center gap-3 group">
                      <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300">
                        <i className="ri-shield-check-line text-xl"></i>
                      </div>
                      <span>Saugus mokėjimas per Stripe</span>
                    </div>
                    <div className="flex items-center gap-3 group">
                      <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300">
                        <i className="ri-gift-line text-xl"></i>
                      </div>
                      <span>Galima pirkti kaip dovaną</span>
                    </div>
                  </div>

                  <div className="border-t border-white/20 pt-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold relative">
                        14,90 €
                        {/* Shimmer effect on price */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                      </span>
                    </div>
                    <p className="text-purple-100 text-sm mt-2">
                      Vienkartinis mokėjimas • Be prenumeratos
                    </p>
                  </div>
                </div>
              </div>

              {/* Right side - Form */}
              <div className="p-10 bg-gradient-to-br from-white to-purple-50/30">
                <h4 className="text-2xl font-bold text-gray-900 mb-6">
                  Užpildykite Duomenis
                </h4>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Gift Toggle */}
                  <div className="bg-gradient-to-r from-pink-50 to-purple-50 border-2 border-purple-200 rounded-xl p-4 hover:border-purple-300 transition-colors duration-300">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={isGift}
                          onChange={(e) => setIsGift(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-12 h-6 bg-gray-300 rounded-full peer-checked:bg-gradient-to-r peer-checked:from-purple-500 peer-checked:to-pink-500 transition-all duration-300"></div>
                        <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 peer-checked:translate-x-6 shadow-md"></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-gift-line text-2xl text-purple-600 group-hover:scale-110 transition-transform duration-300"></i>
                        <span className="font-semibold text-gray-900">Pirkti kaip dovaną</span>
                      </div>
                    </label>
                  </div>

                  {/* Recipient Info */}
                  <div className={`space-y-6 transition-all duration-500 ${isGift ? 'bg-purple-50/50 border-2 border-purple-200 rounded-xl p-4' : ''}`}>
                    {isGift && (
                      <div className="flex items-center gap-2 text-purple-700 font-semibold mb-2">
                        <i className="ri-user-heart-line text-xl"></i>
                        <span>Gavėjo duomenys</span>
                      </div>
                    )}

                    {/* Email - Required */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        {isGift ? 'Gavėjo el. paštas' : 'El. paštas'} <span className="text-pink-500">*</span>
                      </label>
                      <div className="relative group">
                        <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl group-focus-within:text-purple-500 group-focus-within:scale-110 transition-all duration-300"></i>
                        <input
                          type="email"
                          id="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all duration-300 text-base hover:border-purple-300"
                          placeholder="jusu@pastas.lt"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {isGift ? 'Į šį el. paštą bus išsiųsta knyga' : 'Į šį el. paštą atsiųsime knygą PDF formatu'}
                      </p>
                    </div>

                    {/* Name - Required */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                        {isGift ? 'Gavėjo vardas ir pavardė' : 'Vardas ir pavardė'} <span className="text-pink-500">*</span>
                      </label>
                      <div className="relative group">
                        <i className="ri-user-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl group-focus-within:text-purple-500 group-focus-within:scale-110 transition-all duration-300"></i>
                        <input
                          type="text"
                          id="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none transition-all duration-300 text-base hover:border-purple-300"
                          placeholder="Vardas Pavardė"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Gifter Info - Only shown when isGift is true */}
                  {isGift && (
                    <div className="space-y-6 bg-amber-50/50 border-2 border-amber-200 rounded-xl p-4 animate-fade-in-up">
                      <div className="flex items-center gap-2 text-amber-700 font-semibold mb-2">
                        <i className="ri-user-smile-line text-xl"></i>
                        <span>Jūsų duomenys (dovanotojas)</span>
                      </div>

                      {/* Gifter Email */}
                      <div>
                        <label htmlFor="gifterEmail" className="block text-sm font-semibold text-gray-700 mb-2">
                          Jūsų el. paštas <span className="text-pink-500">*</span>
                        </label>
                        <div className="relative group">
                          <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl group-focus-within:text-amber-500 group-focus-within:scale-110 transition-all duration-300"></i>
                          <input
                            type="email"
                            id="gifterEmail"
                            required={isGift}
                            value={formData.gifterEmail}
                            onChange={(e) => setFormData({ ...formData, gifterEmail: e.target.value })}
                            className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-4 focus:ring-amber-100 outline-none transition-all duration-300 text-base hover:border-amber-300"
                            placeholder="jusu@pastas.lt"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Gavėjas žinos, kad dovana nuo jūsų
                        </p>
                      </div>

                      {/* Gifter Name */}
                      <div>
                        <label htmlFor="gifterName" className="block text-sm font-semibold text-gray-700 mb-2">
                          Jūsų vardas ir pavardė <span className="text-pink-500">*</span>
                        </label>
                        <div className="relative group">
                          <i className="ri-user-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl group-focus-within:text-amber-500 group-focus-within:scale-110 transition-all duration-300"></i>
                          <input
                            type="text"
                            id="gifterName"
                            required={isGift}
                            value={formData.gifterName}
                            onChange={(e) => setFormData({ ...formData, gifterName: e.target.value })}
                            className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-4 focus:ring-amber-100 outline-none transition-all duration-300 text-base hover:border-amber-300"
                            placeholder="Vardas Pavardė"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Error message */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2 animate-shake">
                      <i className="ri-error-warning-line text-lg"></i>
                      <span>{error}</span>
                    </div>
                  )}

                  {/* Submit button with enhanced animations */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white py-5 rounded-xl font-bold text-lg hover:from-purple-700 hover:via-purple-600 hover:to-pink-600 transition-all duration-300 shadow-lg hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 whitespace-nowrap group animate-gradient animate-pulse-glow cursor-pointer"
                  >
                    {isLoading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-2xl"></i>
                        <span>Kraunama...</span>
                      </>
                    ) : (
                      <>
                        <i className={`text-2xl group-hover:scale-110 transition-transform duration-300 ${isGift ? 'ri-gift-line' : 'ri-shopping-cart-line'}`}></i>
                        <span>{isGift ? 'Dovanoti Dabar' : 'Įsigyti Dabar'} • 14,90 €</span>
                      </>
                    )}
                  </button>

                  {/* Promo code info */}
                  <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 text-sm text-purple-700 flex items-start gap-2 hover:border-purple-300 transition-colors duration-300">
                    <i className="ri-coupon-line text-lg mt-0.5 flex-shrink-0"></i>
                    <span>Nuolaidų kodą galėsite įvesti mokėjimo puslapyje</span>
                  </div>

                  {/* Security badges */}
                  <div className="flex items-center justify-center gap-6 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-sm text-gray-600 group">
                      <i className="ri-lock-line text-lg text-purple-600 group-hover:scale-110 transition-transform duration-300"></i>
                      <span>Saugus mokėjimas</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 group">
                      <i className="ri-shield-check-line text-lg text-purple-600 group-hover:scale-110 transition-transform duration-300"></i>
                      <span>Stripe apsauga</span>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Additional info */}
          <div className="mt-12 text-center">
            <p className="text-white/80 text-sm">
              Turite klausimų? Rašykite:{' '}
              <a
                href="mailto:giedrius@giedriuspetrauskas.com"
                className="text-amber-400 hover:text-amber-300 font-semibold underline transition-colors cursor-pointer"
              >
                giedrius@giedriuspetrauskas.com
              </a>
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(-5deg); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>
    </section>
  );
}
